import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { ReasonCode } from "@enums/ReasonCode";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { ColourService } from "@services/colour.service";
import { DisplayService, IDialogOptionsJson } from "@services/display.service";
import { GoogleService } from "@services/google.service";
import { GoogleMarkerService, MarkerAnchorPosition } from "@services/googleMarker.service";
import { MapService } from "@services/map.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { IPolyline } from "@typings/google.maps.angular";
import * as moment from "moment";
import { VideoStatus } from "./enums/VideoStatus";
import { IVideoEntry } from "./models/IVideoEntry";
import { VideoPlayerResult } from "./models/video-player-result";
import { IAvailableVideoFootageSection } from "./requestVideo/requestVideo.service";
import { VideoEntryService } from "./services/video-entry.service";

export interface IRequestVideoDialogOptions {
    vehicle: IVehicle;
    requestVideoTime: moment.Moment;
    requestLengthInSeconds?: number;

    onRequestVideoCancelled?: () => void;
    onRequestVideoSent?: () => void;
}

export class CameraDisplayService {
    private readonly videoEntryService = new HybridServiceAccessor<VideoEntryService>("videoEntryService", this.$injector);

    static $inject = ["$q", "$mdDialog", "$mdToast", "googleMarkerService", "displayService", "googleService", "mapService", "colourService", "veriDate", "translationService", "$injector"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly $mdToast: angular.material.IToastService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly displayService: DisplayService,
        private readonly googleService: GoogleService,
        private readonly mapService: MapService,
        private readonly colourService: ColourService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
        private readonly $injector: angular.auto.IInjectorService,) {
    }

    createEventFromVideo(videoEntry: IVideoEntry): IPlotEvent {
        const plotEvent = this.googleMarkerService.createEventMarker(
            this.createPlotFromGpsData(videoEntry),
            {
                label: this.translationService.t(`cameraDisplayService.videoEventLabel`, videoEntry.displayEventCode),
                markerClass: "marker-event-video",
                subIcon: "videocam",
                showLabel: false,
                imageUrl: videoEntry.Status === VideoStatus.Downloaded ? videoEntry.thumbnailUrl : null,
                anchorPosition: MarkerAnchorPosition.Top,
            }, videoEntry);

        return plotEvent;
    }

    showVideoPlayerById(entryId: number, $event: MouseEvent): angular.IPromise<boolean> {
        const promise = this.videoEntryService.service.getVideoEntry(entryId) as angular.IPromise<IVideoEntry>;

        return promise.then(entry => {
            return this.showVideoPlayer(entry, $event).then(x => x.requiresUpdate || x.statusChanged);
        });
    }

    showVideoPlayer(videoEntry: IVideoEntry, $event: MouseEvent = null, showCoaching = false): angular.IPromise<VideoPlayerResult> {
        let statusChanged = false;

        const controller = ($mdDialog: angular.material.IDialogService) => {
            return {
                hide: (requiresUpdate: boolean) => { $mdDialog.hide(requiresUpdate); },
                onStatusUpdate: () => { statusChanged = true },
                selectedVideo: videoEntry,
                showCoaching: showCoaching
            }
        };

        controller.$inject = ["$mdDialog"];

        const dialogHTML = `
            <composite-video-player 
                flex 
                video="$ctrl.selectedVideo" 
                show-coaching="$ctrl.showCoaching" 
                on-delete="$ctrl.hide(true)" 
                on-status-update="$ctrl.onStatusUpdate()">
            <composite-video-player>`;

        const dialogTitle = `${this.veriDate.formatDate(videoEntry?.VideoStartTime)}`;

        const eventDescription = videoEntry?.mp4VideoUrl 
            ? this.translationService.t("cameraDisplayService.video") 
            : this.translationService.t("cameraDisplayService.event");

        const vehicleVideoCard =
            `<span class="text-muted ml-10">${videoEntry?.displayEventCode} ${eventDescription}</span>
             <driver-summary-panel 
                [vehicle]="$ctrl.selectedVideo.vehicle" 
                [driver]="$ctrl.selectedVideo.driver" 
                [compact-view]="true" 
                [light-text]="true" 
                [time]="$ctrl.selectedVideo.VideoStartTime">
            </driver-summary-panel>`;

        const options: IDialogOptionsJson = {
            title: dialogTitle,
            dialogHTML: dialogHTML,
            dialogId: "playVideoDialog",
            afterTitle: vehicleVideoCard
        };

        const dialogOptions: angular.material.IDialogOptions = {
            targetEvent: $event,
            escapeToClose: true,
            clickOutsideToClose: true,
            multiple: true,
            template: this.displayService.createDialogWrapper(options), // Use scope
            controller: controller,
            controllerAs: "$ctrl"
        };

        return this.$mdDialog.show(dialogOptions).then((requiresUpdate: boolean) => {
            return { requiresUpdate, statusChanged };
        });
    }

    showRequestVideoDialog($event: MouseEvent, options: IRequestVideoDialogOptions) {
        const dialogOptions: IDialogOptionsJson = {
            title: this.translationService.t(`Videos.RequestVideo`),
            dialogHTML: `<request-video flex ` +
                `vehicle="$ctrl.vehicle" ` +
                `request-video-time="$ctrl.requestVideoTime" ` +
                `request-length-in-seconds="$ctrl.requestLengthInSeconds" ` +
                `on-request-cancelled="$ctrl.onRequestVideoCancelled() $ctrl.hide();" ` +
                `on-request-sent="$ctrl.onRequestVideoSent()"></request-video2>`,
            dialogId: `requestVideo2Dialog`,
            bindings: {
                vehicle: options?.vehicle,
                requestVideoTime: options?.requestVideoTime,
                requestLengthInSeconds: options?.requestLengthInSeconds,
                onRequestVideoCancelled: () => {
                    options?.onRequestVideoCancelled();
                },
                onRequestVideoSent: () => {
                    options?.onRequestVideoSent();
                },
            },
            event: $event,
            customClass: `request-video-dialog`
        };

        this.displayService.showDialog(dialogOptions);
    }

    showNewVideoToast(videoEntry: IVideoEntry) {
        const controller = () => {
            return {
                playVideo: (video: IVideoEntry, $event: MouseEvent) => {
                    this.$mdToast.hide();
                    this.showVideoPlayer(video, $event);
                },
                video: videoEntry
            }
        };

        var options: angular.material.IToastOptions = {
            controller: controller,
            controllerAs: "$ctrl",
            hideDelay: /*5 **/ 20 * 1000,
            position: "bottom right",
            template: require("./videoToast.html")
        };

        this.$mdToast.show(options);
    }

    createPolylinesFromPlots(plots: IPlot[] = [], addSnapping = true): angular.IPromise<IPolyline> {
        const createdPolyline = this.mapService.createPolylineFromPlots(plots);

        if (addSnapping) {
            return this.addSnapping(createdPolyline);
        }

        return this.$q.resolve(createdPolyline);
    }

    setFootagePercentages(footage: IAvailableVideoFootageSection[]) {
        if (!footage?.length) {
            return;
        }

        footage.forEach(footage => {
            var startP = this.getFootagePositionFromTime(footage.startTime);
            var endP = this.getFootagePositionFromTime(footage.endTime);

            footage.startPercentage = startP;
            footage.widthPercentage = endP - startP;

            // Ensure we cannot go below 0% left
            if (footage.startPercentage < 0) {
                footage.startPercentage = 0;
            }

            // Ensure that we cannot go over 100%
            if (footage.startPercentage + footage.widthPercentage > 100) {
                footage.widthPercentage -= footage.startPercentage + footage.widthPercentage - 100;
            }
        });
    }

    private getFootagePositionFromTime(time: moment.Moment): number {
        const percentPerMinute = 100 / (24 * 60);

        let position = this.veriDate.getMinuteOfDayFromMoment(time) * percentPerMinute;

        position = Math.max(0, position);
        position = Math.min(100, position);

        return position;
    }

    private addSnapping(polyline: IPolyline): angular.IPromise<IPolyline> {

        if (polyline.path.length > 5) {
            polyline.path = [polyline.path[0], polyline.path[polyline.path.length - 1]];
        }

        return this.googleService.snapAndInterpolatePath(polyline.path).then(path => {
            polyline.path = path;
            return polyline;
        }).catch(() => {
            console.error("Unable to snap to road use return default");
            return polyline;
        });
    }

    private createPolyline(plots: IPlot[]): IPolyline {
        const lineColor = this.colourService.redColor;
        const latLngArray = this.mapService.convertPlotsToGoogleLatLng(plots);
        const newPolyline = this.mapService.createPolyline(0, latLngArray, lineColor, null, false); //!relevant
        this.mapService.addDirectionArrows(newPolyline, lineColor);

        return newPolyline;
    }

    private createPlotFromGpsData(videoEntry: IVideoEntry): IPlot {
        let heading = 0;
        let velocity = 0;

        if (videoEntry.VideoExtensionData && videoEntry.VideoExtensionData.VideoGpsData && videoEntry.VideoExtensionData.VideoGpsData.length !== 0) {
            heading = videoEntry.VideoExtensionData.VideoGpsData[0].Heading;
            velocity = videoEntry.VideoExtensionData.VideoGpsData[0].Speed;
        }

        return {
            Direction: heading,
            Latitude: videoEntry.Latitude,
            Longitude: videoEntry.Longitude,
            Reason: ReasonCode.CameraEvent,
            Velocity: velocity,
            Utc: moment(videoEntry.EventTimeUtc).toString(),
            UtcMoment: moment(videoEntry.EventTimeUtc),
            Id: Math.floor(Math.random() * 1000),
            LocationInformation: {
                LocationDescription: videoEntry.PlaceName,
                GeofenceIds: [],
            }
        };
    }
}

